import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Mic, Users, BookOpen, Shield, Wifi } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const TOPICS = [
  {
    icon: Users,
    title: "The Great Wealth Transfer\u2122 \u2014 What Minority Entrepreneurs Must Do Right Now",
    desc: "A data-driven, action-oriented keynote on the $124 trillion wealth transfer and the 6 million businesses changing hands by 2035 \u2014 and exactly how minority entrepreneurs can position themselves to participate.",
    audience: "CDFIs, HBCUs, MBDA Business Centers, minority business conferences, faith-based economic empowerment events",
  },
  {
    icon: BookOpen,
    title: "From Earner to Owner \u2014 The Mindset and Mechanics of Business Acquisition",
    desc: "The practical framework for acquiring an existing business with limited capital \u2014 covering valuation, SBA financing, seller notes, and the first 90 days of ownership.",
    audience: "Workforce development, reentry programs, SBA SBDC events, minority business conferences",
  },
  {
    icon: Shield,
    title: "Financial Literacy as a Civil Rights Issue",
    desc: "Why financial literacy is not a personal responsibility conversation \u2014 it is a structural equity conversation \u2014 and what organizations can do to close the gap.",
    audience: "Community organizations, correctional education programs, nonprofit conferences, First Step Act stakeholder events",
  },
  {
    icon: Mic,
    title: "AI Governance for the Real World \u2014 What Leaders Need to Know",
    desc: "A practitioner\u2019s guide to AI governance for executives and decision-makers navigating policy, risk, and operational implications of AI at scale.",
    audience: "Enterprise technology conferences, DoD and government agency events, executive leadership forums",
  },
  {
    icon: Wifi,
    title: "VALORLink\u2122 \u2014 Technology in Service of Veterans in Crisis",
    desc: "The story of building VALOR and VALORLink\u2122 \u2014 a tech-forward approach to veteran crisis response that leverages the efficiency of ride-sharing platforms to connect veterans with peer support in real time.",
    audience: "Veteran service organizations, VA-affiliated events, nonprofit technology forums",
  },
];

const FORMATS = ["Keynote (45\u201360 min)", "Workshop/Breakout (90 min)", "Panel", "Virtual/Webinar", "Corporate Training (half or full day)"];

export default function Speaking() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", preferred_contact: "", best_time: "",
    organization: "", event_name: "", event_date: "", format: "",
    audience_size: "", location: "", budget_range: "", message: "",
  });
  const [status, setStatus] = useState("");

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.event_name) { setStatus("Please fill required fields."); return; }
    try {
      await api.post("/speaking", form);
      setStatus("Booking request submitted! Expect a response within 5\u20137 business days.");
      setForm({ name: "", email: "", phone: "", preferred_contact: "", best_time: "", organization: "", event_name: "", event_date: "", format: "", audience_size: "", location: "", budget_range: "", message: "" });
      setTimeout(() => setStatus(""), 6000);
    } catch { setStatus("Error submitting. Try again."); }
  };

  return (
    <div>
      {/* HERO */}
      <SEO
        title="Speaking"
        description="Book Dr. Ben Magee for keynotes, workshops, and panels on the Great Wealth Transfer, minority entrepreneurship, financial literacy, AI governance, and veteran crisis response."
        keywords="Dr. Ben Magee speaker, keynote speaker, Great Wealth Transfer keynote, AI governance speaker, minority entrepreneurship, veteran advocacy"
        page="speaking"
        canonical="https://drbenmagee.com/speaking"
      />
      <section className="bg-[#1B2A4A] py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src="https://images.unsplash.com/photo-1560523159-fbe43c0b465f?auto=format&w=1200" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="w-1 h-12 bg-[#C9A84C] mb-8" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" data-testid="speaking-hero-title">
            Bringing Data, Strategy, and Real-World Credibility to Your Stage
          </h1>
        </div>
      </section>

      {/* SPEAKER BIO */}
      <section className="py-16 md:py-24 bg-white" data-testid="speaker-bio">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-6">Speaker Bio</p>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="flex-shrink-0 w-full md:w-auto flex justify-center md:justify-start">
              <img
                src="https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/d5hw9875_9D68558F-415B-4B81-BC77-5CFE62AE5B4C.png"
                alt="Dr. Ben Magee"
                className="w-full max-w-[280px] md:w-40 h-auto md:h-48 object-cover object-top rounded-sm border-2 border-[#C9A84C]/30"
              />
            </div>
            <p className="text-base text-[#1B2A4A]/70 leading-relaxed">
            Dr. Ben Magee has been studying the economics of the Great Wealth Transfer since 2019 &mdash; years before it entered the mainstream conversation. His forthcoming book, THE GREAT WEALTH TRANSFER&trade;, is the first comprehensive acquisition playbook written specifically for minority and underserved entrepreneurs. A U.S. Army veteran, PhD, CISSP-certified technologist, MENSA International member, and founder of multiple organizations spanning technology, publishing, national security, and veteran advocacy, Dr. Magee brings a rare combination of analytical rigor and operational credibility to every stage he occupies.
          </p>
          </div>
        </div>
      </section>

      {/* SPEAKING TOPICS */}
      <section className="py-20 md:py-32 bg-[#E8EDF5]" data-testid="speaking-topics">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Speaking Topics</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            Available Keynotes & Workshops
          </h2>
          <div className="space-y-6">
            {TOPICS.map((t, i) => (
              <motion.div
                key={t.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-30px" }}
                variants={fadeUp}
                className="bg-white border border-[#1B2A4A]/10 rounded-sm p-8 hover:-translate-y-0.5 transition-transform duration-300"
              >
                <div className="flex items-start gap-4">
                  <t.icon className="h-8 w-8 text-[#C9A84C] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-2">{t.title}</h3>
                    <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-3">{t.desc}</p>
                    <p className="text-xs text-[#1B2A4A]/50"><span className="font-semibold text-[#C9A84C]">Ideal for:</span> {t.audience}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Available Formats</p>
          <div className="flex flex-wrap gap-3">
            {FORMATS.map((f) => (
              <Badge key={f} variant="secondary" className="bg-[#E8EDF5] text-[#1B2A4A] rounded-sm px-4 py-2 text-sm">{f}</Badge>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section className="py-20 md:py-32 bg-[#F5EDD6]" data-testid="speaking-booking-form">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Book Dr. Magee</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-8">
            Speaking Engagement Request
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Name *</Label><Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="speaking-name" className="rounded-sm border-[#1B2A4A]/20" /></div>
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Email *</Label><Input type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} data-testid="speaking-email" className="rounded-sm border-[#1B2A4A]/20" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Phone Number</Label><Input type="tel" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} data-testid="speaking-phone" className="rounded-sm border-[#1B2A4A]/20" /></div>
              <div>
                <Label className="text-[#1B2A4A] text-sm mb-1.5 block">Preferred Contact Method</Label>
                <Select onValueChange={(v) => handleChange("preferred_contact", v)}>
                  <SelectTrigger data-testid="speaking-preferred-contact" className="rounded-sm border-[#1B2A4A]/20"><SelectValue placeholder="Select preference" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Phone">Phone</SelectItem>
                    <SelectItem value="Either">Either</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1B2A4A] text-sm mb-1.5 block">Best Time to Reach You</Label>
                <Select onValueChange={(v) => handleChange("best_time", v)}>
                  <SelectTrigger data-testid="speaking-best-time" className="rounded-sm border-[#1B2A4A]/20"><SelectValue placeholder="Select time" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Morning (9am-12pm)">Morning (9am-12pm)</SelectItem>
                    <SelectItem value="Afternoon (12pm-5pm)">Afternoon (12pm-5pm)</SelectItem>
                    <SelectItem value="Evening (5pm-8pm)">Evening (5pm-8pm)</SelectItem>
                    <SelectItem value="Anytime">Anytime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Organization</Label><Input value={form.organization} onChange={(e) => handleChange("organization", e.target.value)} data-testid="speaking-org" className="rounded-sm border-[#1B2A4A]/20" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Event Name *</Label><Input value={form.event_name} onChange={(e) => handleChange("event_name", e.target.value)} data-testid="speaking-event" className="rounded-sm border-[#1B2A4A]/20" /></div>
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Event Date</Label><Input type="date" value={form.event_date} onChange={(e) => handleChange("event_date", e.target.value)} data-testid="speaking-date" className="rounded-sm border-[#1B2A4A]/20" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1B2A4A] text-sm mb-1.5 block">Format</Label>
                <Select onValueChange={(v) => handleChange("format", v)}>
                  <SelectTrigger data-testid="speaking-format" className="rounded-sm border-[#1B2A4A]/20"><SelectValue placeholder="Select format" /></SelectTrigger>
                  <SelectContent>{FORMATS.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Audience Size</Label><Input value={form.audience_size} onChange={(e) => handleChange("audience_size", e.target.value)} data-testid="speaking-audience" className="rounded-sm border-[#1B2A4A]/20" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Location</Label><Input value={form.location} onChange={(e) => handleChange("location", e.target.value)} data-testid="speaking-location" className="rounded-sm border-[#1B2A4A]/20" /></div>
              <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Budget Range</Label><Input value={form.budget_range} onChange={(e) => handleChange("budget_range", e.target.value)} data-testid="speaking-budget" className="rounded-sm border-[#1B2A4A]/20" /></div>
            </div>
            <div><Label className="text-[#1B2A4A] text-sm mb-1.5 block">Message</Label><Textarea value={form.message} onChange={(e) => handleChange("message", e.target.value)} data-testid="speaking-message" className="rounded-sm border-[#1B2A4A]/20 min-h-[120px]" /></div>
            <Button type="submit" data-testid="speaking-submit-btn" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm px-8">
              Submit Booking Request
            </Button>
            {status && <p className="text-sm text-[#1B2A4A] font-medium mt-2">{status}</p>}
          </form>
        </div>
      </section>
    </div>
  );
}
